import { isTokenValid, refreshAuthToken } from '@/services/api/apiAuthHelpers'
import { globals } from '@/main.ts'

// Blueprint API Error Codes
export type BlueprintErrorCode =
	| 'BP-NFA-001'
	| 'BP-NFA-002'
	| 'BP-NFA-003'
	| 'BP-NFA-0040'
	| 'BP-NFA-0041'
	| 'BP-NFA-0042'
	| 'BP-NFA-0050'
	| 'BP-NFA-0051'
	| 'BP-NFA-0060'
	| 'BP-NFA-0061'
	| 'BP-NFA-0062'
	| 'BP-NFA-0063'
	| 'BP-NFA-0064'
	| 'BP-NFA-0065'
	| 'BP-NFA-007'
	| 'BP-NFA-008'
	| 'BP-NFA-009'
	| 'BP-NFA-010'
	| 'BP-NFA-0110'
	| 'BP-NFA-0111'
	| 'BP-NFA-012'
	| 'BP-NFA-013'
	| 'BP-NFA-014'
	| 'BP-NFA-015'
	| 'BP-NFA-0160'
	| 'BP-NFA-0161'
	| 'BP-NFA-0162'
	| 'BP-NFA-017'
	| 'BP-NFA-018'
	| 'BP-NFA-0181'
	| 'BP-NFA-0190'
	| 'BP-NFA-0191'
	| 'BP-NFA-0200'
	| 'BP-NFA-0201'
	| 'BP-NFA-021'
	| 'BP-NFA-022'
	| 'BP-NFA-0231'
	| 'BP-NFA-0232'
	| 'BP-NFA-0233'
	| 'BP-NFA-0234'
	| 'BP-NFA-0235'
	| 'BP-NFA-0236'
	| 'BP-NFA-0237'
	| 'BP-NFA-0238'
	| 'BP-NFA-0240'
	| 'BP-NFA-100'
	| 'BP-NFA-0250'
	| 'BP-NFA-0251'
	| 'BP-NFA-0253'

// Caremark API Error Codes
export type CaremarkErrorCode =
	| 'BP-CARE-001'
	| 'BP-CARE-002'
	| 'BP-CARE-003'
	| 'BP-CARE-004'
	| 'BP-CARE-005'
	| 'BP-CARE-006'
	| 'BP-CARE-007'
	| 'BP-CARE-008'

// FHIR API Error Codes
export type FhirErrorCode =
	| 'BP-FA-001'
	| 'BP-FA-002'
	| 'BP-FA-003'
	| 'BP-FA-004'
	| 'BP-FA-005'
	| 'BP-FA-006'
	| 'BP-FA-007'
	| 'BP-FA-008'
	| 'BP-FA-009'
	| 'BP-FA-010'

// Combined type
export type ApiErrorCode = BlueprintErrorCode | CaremarkErrorCode | FhirErrorCode

export interface ApiParams {
	url: string
	type?: 'mbp' | 'fhir'
	method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD' | 'OPTIONS'
	headers?: Record<string, string>
	body?: unknown
	customError?: CustomError
	errorCode?: ApiErrorCode
}

export interface ValidationError {
	code: string
	propertyName?: string
	message: string
}

interface CustomError {
	type?: number
	message?: string
	suppressModal: boolean
}

export interface ResponseCustomError extends ErrorConstructor {
	cause: {
		eCode: string
		response: string
		customError: CustomError
		validationErrors: string[]
	}
}

export interface ApiValidationError extends Error {
	cause: {
		eCode: ApiErrorCode
		response: Response | null
		customError?: CustomError
		validationErrors: ValidationError[]
	}
}

/** Base url for all api calls */
const baseURL = import.meta.env.VITE_APIGEE_BASE_URL
const fhirUrl = import.meta.env.VITE_PST_FHIR_URL
const userActivityEvent = new Event('user-activity')

/**
 * Normalizes caught errors and rethrows with structured cause metadata.
 * Preserves errorCode and customError from the original ApiParams for downstream handling.
 */
const handleApiError = (
	error: unknown,
	errorCode?: ApiErrorCode,
	customError?: CustomError
): never => {
	// Setup data
	const errorRes = (error as any)?.response ?? null
	const validationErrors: ValidationError[] = (error as any)?.validationErrors ?? []
	const eCode: ApiErrorCode = errorCode ?? 'BP-NFA-100'
	const message = error instanceof Error ? error.message : 'An unexpected API error occurred'

	// Trigger global error modal
	if (!customError?.suppressModal && customError?.type != 422) globals.$Error(true, eCode)

	// Throw error
	throw new Error(message, {
		cause: {
			eCode,
			response: errorRes,
			customError,
			validationErrors // always present, empty array if none
		}
	})
}

export const apiWrapper = async (params: ApiParams, mockUrl?: string) => {
	const { url, method, headers, body, customError, errorCode } = params
	const bearerToken = sessionStorage.getItem('access_token')

	window.dispatchEvent(userActivityEvent)

	try {
		if (sessionStorage.getItem('access_token') && !isTokenValid()) {
			await refreshAuthToken()
		}

		let fetchUrl = `${baseURL}${url}`
		if (mockUrl) fetchUrl = `${mockUrl}${url}`
		else if (params.type === 'mbp') fetchUrl = `${baseURL}/member/myblueprint/v1${url}`
		else if (params.type === 'fhir') fetchUrl = `${fhirUrl}${url}`

		const response = await fetch(fetchUrl, {
			method,
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
				...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
				...(mockUrl ? { 'X-API-Key': import.meta.env.VITE_PST_KEY } : {}),
				...headers
			},
			...(body ? { body: JSON.stringify(body) } : {})
		})

		if (!response.ok) {
			const json = await response.json().catch(() => null)
			const err = Object.assign(new Error(`HTTP ${response.status}`), {
				response,
				validationErrors: json?.validationErrors ?? []
			})
			throw err
		}

		// Clone so the caller can still consume the response if needed
		const cloned = response.clone()
		const json = await cloned.json().catch(() => null)

		if (json?.validationErrors?.length) {
			const err = Object.assign(new Error('API validation errors'), {
				response,
				validationErrors: json.validationErrors
			})
			throw err
		}

		return response
	} catch (error) {
		return handleApiError(error, errorCode, customError)
	}
}
